// Campo "Persona": nombre y foto (más correo y teléfono opcionales) guardados
// en el propio registro, sin colección aparte. Se guarda como JSON string con
// una lista `[{"name":"Ana","image":"<storageId>"}]` (una sola persona salvo
// que el campo sea `multiple`), igual que los campos de lista, para caber en
// `customFieldValue`. Lógica pura: la usan el servidor (validación, búsqueda,
// PDF, reportes) y el navegador (formulario, tabla, tarjetas, cronograma).

export interface PersonValue {
  /**
   * Persona de una lista del campo o de una colección (`personSource`): id de
   * la entrada o del registro. El nombre guardado es una copia para buscar,
   * exportar y el PDF; la tabla y el formulario muestran el actual.
   */
  ref?: string;
  name: string;
  /** Foto: storageId de Convex. */
  image?: string;
  email?: string;
  phone?: string;
}

/** Personas por campo múltiple. */
export const MAX_PEOPLE = 20;
const MAX_NAME = 120;
const MAX_EMAIL = 200;
const MAX_PHONE = 40;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Formato de un storageId (`_`: ids de convex-test). */
const STORAGE_ID_RE = /^[a-z0-9_]{20,64}$/;

const clean = (value: unknown, max: number) =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ").slice(0, max) : "";

/**
 * Personas de un valor guardado (o recibido): JSON de una lista o de una
 * persona, objetos, o nombres sueltos separados por coma (import, agente).
 * Descarta las que no tienen nombre; no valida (ver `normalizePeople`).
 */
export function parsePeople(raw: unknown): PersonValue[] {
  let value: unknown = raw;
  if (typeof raw === "string") {
    const text = raw.trim();
    if (!text) return [];
    try {
      value = JSON.parse(text);
    } catch {
      value = text.split(",").map((name) => ({ name }));
    }
  }
  const list = Array.isArray(value) ? value : value && typeof value === "object" ? [value] : [];
  const out: PersonValue[] = [];
  for (const item of list) {
    const obj = (typeof item === "string" ? { name: item } : item) as Record<string, unknown> | null;
    if (!obj || typeof obj !== "object") continue;
    const name = clean(obj.name, MAX_NAME + 1);
    const ref = typeof obj.ref === "string" ? obj.ref.trim() : "";
    if (!name && !ref) continue;
    const image = typeof obj.image === "string" ? obj.image.trim() : "";
    const email = clean(obj.email, MAX_EMAIL + 1).toLowerCase();
    const phone = clean(obj.phone, MAX_PHONE + 1);
    out.push({
      ...(ref ? { ref } : {}),
      name,
      ...(image ? { image } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    });
  }
  return out;
}

/** Personas válidas y sin repetidas (sin tope); lanza un mensaje si alguna no sirve. */
function validPeople(raw: unknown, linked: boolean): PersonValue[] {
  const seen = new Set<string>();
  const unique: PersonValue[] = [];
  for (const parsed of parsePeople(raw)) {
    const person: PersonValue = linked ? parsed : { ...parsed, ref: undefined };
    if (!person.ref) delete person.ref;
    if (!person.name && !person.ref) continue;
    if (person.name.length > MAX_NAME) throw new Error(`el nombre admite hasta ${MAX_NAME} caracteres`);
    if (person.image && !STORAGE_ID_RE.test(person.image)) throw new Error("la foto no es una imagen válida");
    if (person.email && (person.email.length > MAX_EMAIL || !EMAIL_RE.test(person.email))) {
      throw new Error(`"${person.email}" no es un correo válido`);
    }
    if (person.phone && person.phone.length > MAX_PHONE) throw new Error(`el teléfono admite hasta ${MAX_PHONE} caracteres`);
    const key = personKey(person);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(person);
  }
  return unique;
}

/**
 * Valor a guardar: valida cada persona, quita repetidas (mismo nombre y
 * correo, o misma `ref`) y respeta el tope. `undefined` si queda vacío; lanza
 * un mensaje si algo no es válido. Con `linked` (lista o colección) cada
 * persona es una `ref` (o un nombre a buscar en la lista, ver
 * `resolveCustomFieldValues`); sin él, la `ref` se descarta.
 */
export function normalizePeople(raw: unknown, multiple: boolean, linked = false): string | undefined {
  const unique = validPeople(raw, linked).filter((p) => linked || p.name);
  if (unique.length === 0) return undefined;
  if (!multiple && unique.length > 1) throw new Error("admite una sola persona");
  if (unique.length > MAX_PEOPLE) throw new Error(`admite hasta ${MAX_PEOPLE} personas`);
  return JSON.stringify(unique);
}

/** Identidad de una persona dentro de un campo: su `ref` o nombre y correo, sin mayúsculas. */
export function personKey(person: Pick<PersonValue, "name" | "email" | "ref">): string {
  return person.ref ? `#${person.ref}` : `${person.name.toLowerCase()}|${person.email ?? ""}`;
}

// ── Fuente de las personas (`personSource` del campo) ──────────────────

/** Personas de la lista del campo (tope). */
export const MAX_PERSON_OPTIONS = 200;

/** Entrada de la lista del campo: una persona con su `ref` (id estable). */
export type PersonOption = PersonValue & { ref: string };

/** Fuente de un campo Persona (`personSource`), con los ids como texto. */
export type PersonSourceShape =
  | { kind: "list"; people: PersonValue[] }
  | {
      kind: "collection";
      collectionId: string;
      nameField: string;
      imageField?: string;
      emailField?: string;
      phoneField?: string;
    };

/** Campos de la colección fuente que sirven para cada dato de la persona. */
export const PERSON_SOURCE_FIELD_TYPES = {
  nameField: ["text", "email", "phone", "select"],
  imageField: ["image", "image_multiple"],
  emailField: ["email", "text"],
  phoneField: ["phone", "text"],
} as const;

/**
 * Lista del campo lista para guardar: valida como un valor de varias
 * personas, sin repetidas, y da `ref` a las nuevas (conserva la de las
 * existentes: los registros apuntan a ella).
 */
export function cleanPersonList(raw: unknown, newRef: () => string): PersonOption[] {
  const people = validPeople(raw, true).filter((p) => p.name);
  if (people.length > MAX_PERSON_OPTIONS) throw new Error(`la lista admite hasta ${MAX_PERSON_OPTIONS} personas`);
  const seen = new Set<string>();
  return people.map((p) => {
    let ref = p.ref && !seen.has(p.ref) ? p.ref : newRef();
    while (seen.has(ref)) ref = newRef();
    seen.add(ref);
    return { ...p, ref };
  });
}

/** Nombres separados por coma (búsqueda, PDF, reportes, exportar). */
export function peopleText(raw: unknown): string {
  return parsePeople(raw)
    .map((p) => p.name)
    .join(", ");
}
